import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";

interface CopyNumberButtonProps {
  /** The text to copy. When empty/undefined, nothing is rendered. */
  value?: string | null;
  className?: string;
}

/**
 * Small inline copy-to-clipboard icon meant to sit right after an item
 * number in a list cell. Clicking copies the value and briefly swaps the
 * icon to a green check as feedback. Clicks never bubble to the row.
 */
export function CopyNumberButton({ value, className }: CopyNumberButtonProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  if (!value) return null;
  const text = value;

  function handleClick(e: React.MouseEvent<HTMLElement>) {
    // The surrounding list row navigates on click; never trigger it.
    e.stopPropagation();
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Tooltip
      title={copied ? t("compMisc.copied") : t("compMisc.copyNumber")}
      placement="top"
    >
      <button
        type="button"
        onClick={handleClick}
        aria-label={t("compMisc.copyNumber")}
        className={`shrink-0 inline-flex items-center p-0.5 bg-transparent border-none cursor-pointer leading-none ${
          className ?? ""
        }`}
      >
        <FontAwesomeIcon
          icon={copied ? faCheck : faCopy}
          className={copied ? "text-green-600" : "text-gray-400 hover:text-gray-600"}
          style={{ fontSize: 10 }}
        />
      </button>
    </Tooltip>
  );
}
